/**
 * Utility functions to detect and extract Polymarket market links from text
 */

export interface PolymarketLink {
  url: string;
  slug: string;
  type: 'market' | 'event';
  startIndex: number;
  endIndex: number;
}

/**
 * Regex patterns for Polymarket URLs
 * Captures URLs with optional query parameters (e.g., ?tid=123)
 */
const POLYMARKET_PATTERNS = {
  market: /https?:\/\/(?:www\.)?polymarket\.com\/(?:market|event)\/([a-zA-Z0-9-]+)(?:\?[^\s]*)?/gi,
  event: /https?:\/\/(?:www\.)?polymarket\.com\/event\/([a-zA-Z0-9-]+)(?:\?[^\s]*)?/gi,
};

/**
 * Extract all Polymarket links from text content
 */
export function extractPolymarketLinks(content: string): PolymarketLink[] {
  const links: PolymarketLink[] = [];

  // Find all market/event links
  const marketMatches = content.matchAll(POLYMARKET_PATTERNS.market);

  for (const match of marketMatches) {
    const url = match[0];
    const slug = match[1];
    const startIndex = match.index!;
    const endIndex = startIndex + url.length;

    // Determine if it's a market or event based on URL structure
    const type = url.includes('/event/') ? 'event' : 'market';

    links.push({
      url,
      slug,
      type,
      startIndex,
      endIndex,
    });
  }

  // Remove duplicates based on slug
  const uniqueLinks = links.filter(
    (link, index, self) =>
      index === self.findIndex((l) => l.slug === link.slug)
  );

  return uniqueLinks;
}

/**
 * Check if content contains any Polymarket links
 */
export function hasPolymarketLinks(content: string): boolean {
  return POLYMARKET_PATTERNS.market.test(content);
}

/**
 * Replace Polymarket links in content with a placeholder for rendering
 */
export function replacePolymarketLinksWithPlaceholder(
  content: string
): { processedContent: string; links: PolymarketLink[] } {
  const links = extractPolymarketLinks(content);
  let processedContent = content;

  // Replace links with placeholders (from end to start to maintain indices)
  for (let i = links.length - 1; i >= 0; i--) {
    const link = links[i];
    const placeholder = `__POLYMARKET_CARD_${i}__`;
    processedContent =
      processedContent.slice(0, link.startIndex) +
      placeholder +
      processedContent.slice(link.endIndex);
  }

  return { processedContent, links };
}

/**
 * Extract slug from a Polymarket URL
 */
export function extractSlugFromUrl(url: string): string | null {
  const match = url.match(POLYMARKET_PATTERNS.market);
  if (!match) return null;

  const fullMatch = url.match(/\/(?:market|event)\/([a-zA-Z0-9-]+)/);
  return fullMatch ? fullMatch[1] : null;
}
